/**
 * Reference/lookup data from the SSS backend -- clients, engagements,
 * staff, position rates. Used both to populate filter dropdowns and to
 * join names/positions/rates onto the comparison rows computed in
 * useWipComparison.js.
 */
import { useQuery } from '@tanstack/react-query';
import { sssApiClient } from '../services/apiClient.js';

/**
 * SSS's list endpoints may return a plain array or a Laravel paginator's
 * `{ data: [...] }` wrapper -- handles either without needing to know
 * which in advance.
 */
function unwrapList(data) {
  return Array.isArray(data) ? data : (data?.data ?? []);
}

export const useClients = () =>
  useQuery({
    queryKey: ['sss', 'clients'],
    queryFn: async ({ signal }) => {
      const { data } = await sssApiClient.get('/clients', { signal });
      return unwrapList(data);
    },
    staleTime: 5 * 60_000,
  });

/** @param {{ branch_id?: string }} [filters] */
export const useEngagements = (filters = {}) =>
  useQuery({
    queryKey: ['sss', 'engagements', filters],
    queryFn: async ({ signal }) => {
      const { data } = await sssApiClient.get('/engagements', { signal, params: filters });
      return unwrapList(data);
    },
    staleTime: 2 * 60_000,
  });

/** @param {{ branch_id?: string }} [filters] */
export const useStaffList = (filters = {}) =>
  useQuery({
    queryKey: ['sss', 'staff', filters],
    queryFn: async ({ signal }) => {
      const { data } = await sssApiClient.get('/staff', { signal, params: filters });
      return unwrapList(data);
    },
    staleTime: 2 * 60_000,
  });

export const usePositionRates = () =>
  useQuery({
    queryKey: ['sss', 'position-rates'],
    queryFn: async ({ signal }) => {
      const { data } = await sssApiClient.get('/position-rates', { signal });
      return unwrapList(data);
    },
    staleTime: 10 * 60_000,
  });

/** @param {{ branch_id?: string }} [filters] */
export const useUsers = (filters = {}) =>
  useQuery({
    queryKey: ['sss', 'users', filters],
    queryFn: async ({ signal }) => {
      const { data } = await sssApiClient.get('/users', { signal, params: filters });
      return unwrapList(data);
    },
    staleTime: 2 * 60_000,
  });

/**
 * Real SSS schedule bookings (scheduled hours). Shared by the Dashboard,
 * Engagement WIP Summary, Staff Utilization, and Engagement Progressing
 * pages -- all of these can show real SSS-sourced scheduled-hours data
 * even before TRS's actual-hours integration is wired.
 *
 * Query params CONFIRMED against the live backend (422 response bodies):
 * `start_date`/`end_date` are REQUIRED (not `date_from`/`date_to` as
 * originally guessed), and `branch_id` must be an ARRAY, not a scalar
 * (`"The branch id field must be an array."`). Sent here as the literal
 * key `'branch_id[]'` mapped to a one-item array -- axios's default
 * (bracket-less) array serialization produces repeated `branch_id=1`
 * pairs, which PHP's query-string parser collapses back down to a
 * scalar; only the literal `[]` suffix makes PHP (and therefore
 * Laravel's `array` validation rule) actually see an array.
 *
 * ⚠️ Still unconfirmed: `engagement_id`'s param name/shape, and the
 * response field names (`staff_id`/`user_id`, `hours`/`scheduled_hours`).
 *
 * Callers that want "all schedules" with no real date filter (Dashboard,
 * Engagement WIP Summary, Staff Utilization, Engagement Progressing)
 * don't pass dateFrom/dateTo, so this hook defaults to a wide fallback
 * range (one year back to one year forward) rather than omitting the
 * now-required params.
 * @param {{ branchId?: string|null, engagementId?: string|null, dateFrom?: string, dateTo?: string, enabled?: boolean }} [filters]
 */
export const useSchedules = (filters = {}) => {
  const today = new Date();
  const fallbackStart = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
  const fallbackEnd = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
  const startDate = filters.dateFrom ?? fallbackStart.toISOString().slice(0, 10);
  const endDate = filters.dateTo ?? fallbackEnd.toISOString().slice(0, 10);

  return useQuery({
    queryKey: ['sss', 'schedules', filters.branchId, filters.engagementId, startDate, endDate],
    queryFn: async ({ signal }) => {
      const { data } = await sssApiClient.get('/schedules', {
        signal,
        params: {
          'branch_id[]': filters.branchId ? [filters.branchId] : undefined,
          engagement_id: filters.engagementId ?? undefined,
          start_date: startDate,
          end_date: endDate,
        },
      });
      return unwrapList(data);
    },
    enabled: filters.enabled ?? true,
    staleTime: 30_000,
  });
};

/**
 * Reads a schedule row's staff id and hours regardless of which field
 * name the live response actually uses.
 * @param {any} schedule
 */
export function scheduleStaffId(schedule) {
  return String(schedule.staff_id ?? schedule.user_id ?? '');
}
/** @param {any} schedule */
export function scheduleHours(schedule) {
  return Number(schedule.hours ?? schedule.scheduled_hours ?? 0);
}
/** @param {any} schedule */
export function scheduleEngagementId(schedule) {
  return schedule.engagement_id != null ? String(schedule.engagement_id) : null;
}

/**
 * Working weekdays (Mon-Fri) between two dates, inclusive -- used as a
 * simple capacity estimate (workingDays x 8 hrs) for Staff Utilization.
 * Doesn't account for public holidays (no holiday-calendar endpoint
 * wired here yet), so this slightly overstates true capacity.
 * @param {Date} start
 * @param {Date} end
 */
export function countWorkingDays(start, end) {
  let count = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}
